/**
 * Full-width terrain: rolling hills, mountain peaks, mesas, dunes, farm
 * terraces and a table mountain. Every shape is a crest line generated from
 * smooth noise; the ridge fills everything below its crest.
 */
import type { RidgeShape, RidgesLayer } from '@dropstack/core';
import { clamp, lerp, smoothstep, type SceneSize } from '../layout.ts';
import { hash01, valueNoise } from '../noise.ts';
import { circlePath, rgba, rimOf, shade, smoothLine, strokeRim, vGradient, type Ctx, type Point } from './draw.ts';
import type { Painter } from './types.ts';

type Profile = (xUnits: number, xFrac: number) => number;

/** Rounded peak falloff: 1 at the centre, 0 at |d| = w, slightly concave sides and a soft tip. */
function peak(d: number, w: number): number {
  const tip = w * 0.06;
  const dd = Math.sqrt(d * d + tip * tip) - tip;
  const t = clamp(1 - dd / w, 0, 1);
  return t ** 1.25;
}

/**
 * Height profile (0 = baseline … 1 = full ridge height) for a shape. `xUnits`
 * is x in scene units (features keep their size on any screen); `xFrac` is x
 * as a fraction of the width (for the one landmark shape, the table mountain).
 */
function profileFor(shape: RidgeShape, seed: number, roughness: number): Profile {
  const r = clamp(roughness, 0, 1);
  const jag = (x: number) => r * 0.05 * valueNoise(seed + 91, x * 14);
  switch (shape) {
    case 'rolling':
    case 'terraces':
      return (x) => {
        const a = valueNoise(seed, x * 1.35 + 3.7);
        const b = valueNoise(seed + 1, x * 3.2);
        return clamp(0.6 + 0.4 * (0.72 * a + 0.28 * (0.5 + r) * b) + jag(x) * 0.5, 0.12, 1);
      };
    case 'peaks': {
      const spacing = 0.34;
      return (x) => {
        const k0 = Math.floor(x / spacing);
        let v = 0.12;
        for (let k = k0 - 3; k <= k0 + 3; k++) {
          const c = (k + 0.5 + (hash01(seed, k) - 0.5) * 0.7) * spacing;
          const h = 0.45 + 0.55 * hash01(seed + 7, k);
          const w = spacing * (0.95 + 0.6 * hash01(seed + 13, k));
          v = Math.max(v, h * peak(x - c, w));
        }
        return clamp(v + jag(x), 0.08, 1);
      };
    }
    case 'mesa': {
      const seg = 0.38;
      const level = (k: number) => {
        const h = hash01(seed + 3, k);
        return h < 0.25 ? 0.22 + h : 0.55 + 0.45 * hash01(seed + 5, k);
      };
      return (x) => {
        const u = x / seg + 0.37;
        const k = Math.floor(u);
        const f = u - k;
        const edge = 0.1 + 0.08 * hash01(seed + 11, k);
        const v = lerp(level(k), level(k + 1), smoothstep(1 - edge, 1, f));
        return clamp(v + jag(x) * 0.6, 0.1, 1);
      };
    }
    case 'dunes':
      return (x) => {
        const t = x * 1.7 + 0.25 * valueNoise(seed, x * 0.9);
        const k = Math.floor(t);
        const f = t - k;
        // Long gentle windward slope, short steep lee side.
        const wave = f < 0.68 ? Math.sin((f / 0.68) * (Math.PI / 2)) : Math.cos(((f - 0.68) / 0.32) * (Math.PI / 2));
        const amp = 0.55 + 0.45 * hash01(seed + 17, k);
        return clamp(0.3 + 0.7 * amp * wave ** 1.3, 0.1, 1);
      };
    case 'table':
      return (x, xf) => {
        const d = Math.abs(xf - 0.5);
        const plateau = 0.29;
        const cliff = 0.07;
        let v = 1 - 0.012 * Math.abs(valueNoise(seed, x * 8));
        if (d > plateau) {
          v = 1 - 0.42 * smoothstep(plateau, plateau + cliff, d);
          v -= 0.26 * smoothstep(plateau + cliff, 0.58, d);
          v += 0.06 * valueNoise(seed + 2, x * 6) * smoothstep(plateau, plateau + cliff * 2, d);
        }
        // Flanking summits (think Devil's Peak and Lion's Head).
        v = Math.max(v, 0.74 * peak(xf - 0.08, 0.11), 0.6 * peak(xf - 0.93, 0.07));
        return clamp(v, 0.1, 1);
      };
  }
}

/** Crest line of a ridge in CSS px, sampled across [-bleed, width + bleed]. */
export function ridgeCrest(layer: RidgesLayer, size: SceneSize, seed: number): Point[] {
  const profile = profileFor(layer.shape, seed, layer.roughness ?? 0.5);
  const base = layer.baseline * size.height;
  const h = layer.height * size.height;
  const x0 = -size.bleed - 4;
  const x1 = size.width + size.bleed + 4;
  const step = clamp(size.unit * 0.012, 3, 10);
  const pts: Point[] = [];
  for (let x = x0; x <= x1 + step; x += step) {
    const xx = Math.min(x, x1);
    pts.push({ x: xx, y: base - h * profile(xx / size.unit, xx / size.width) });
    if (xx === x1) break;
  }
  return pts;
}

function ridgeBody(ctx: Ctx, crest: readonly Point[], bottom: number): void {
  const first = crest[0];
  const last = crest[crest.length - 1];
  if (!first || !last) return;
  ctx.beginPath();
  smoothLine(ctx, crest);
  ctx.lineTo(last.x, bottom);
  ctx.lineTo(first.x, bottom);
  ctx.closePath();
}

/** Contiguous runs of the crest that rise above `lineY(x)`. */
function runsAbove(crest: readonly Point[], lineY: (x: number) => number): Point[][] {
  const runs: Point[][] = [];
  let run: Point[] = [];
  for (const p of crest) {
    if (p.y < lineY(p.x)) run.push(p);
    else if (run.length) {
      runs.push(run);
      run = [];
    }
  }
  if (run.length) runs.push(run);
  return runs.filter((r) => r.length > 1);
}

/** Snow caps: white above the snow line with a scalloped lower edge. */
function paintSnow(ctx: Ctx, layer: RidgesLayer, crest: readonly Point[], size: SceneSize, seed: number, snow: string): void {
  const base = layer.baseline * size.height;
  const h = layer.height * size.height;
  const line = base - h * clamp(layer.snowLine ?? 0.62, 0.05, 0.95);
  const wob = size.unit * 0.014;
  const lineY = (x: number) => line + wob * valueNoise(seed + 31, x / size.unit / 0.05) + wob * 0.8 * Math.abs(Math.sin(x / (size.unit * 0.035)));
  for (const run of runsAbove(crest, lineY)) {
    const lower = [...run].reverse().map((p) => ({ x: p.x, y: Math.max(p.y, lineY(p.x)) }));
    ctx.beginPath();
    smoothLine(ctx, run);
    smoothLine(ctx, lower, true);
    ctx.closePath();
    ctx.fillStyle = vGradient(ctx, line - h * 0.5, line + wob, [snow, shade(snow, -0.06)]);
    ctx.fill();
    ctx.beginPath();
    smoothLine(ctx, lower);
    strokeRim(ctx, rgba(shade(snow, -0.2), 0.8), size, 0.7);
  }
}

/** Frosting that follows the crest and drips down (candy themes). */
function paintIcing(ctx: Ctx, crest: readonly Point[], size: SceneSize, seed: number, icing: string): void {
  const thick = size.unit * 0.018;
  const drips: { x: number; len: number; w: number }[] = [];
  const first = crest[0];
  const last = crest[crest.length - 1];
  if (!first || !last) return;
  const gap = size.unit * 0.07;
  for (let i = 0, x = first.x; x < last.x; i++, x += gap * (0.6 + hash01(seed + 41, i))) {
    drips.push({ x, len: size.unit * (0.012 + 0.03 * hash01(seed + 43, i)), w: size.unit * (0.012 + 0.008 * hash01(seed + 47, i)) });
  }
  const dripAt = (x: number) => {
    let d = 0;
    for (const dr of drips) {
      const t = Math.abs(x - dr.x) / dr.w;
      if (t < 1) d = Math.max(d, dr.len * Math.cos((t * Math.PI) / 2) ** 2);
    }
    return d;
  };
  // Resample finely so the narrow drips come out round, not triangular.
  const fine = resample(crest, Math.max(1.5, size.unit * 0.004));
  const lower = fine.reverse().map((p) => ({ x: p.x, y: p.y + thick + dripAt(p.x) }));
  ctx.beginPath();
  smoothLine(ctx, crest);
  smoothLine(ctx, lower, true);
  ctx.closePath();
  ctx.fillStyle = icing;
  ctx.fill();
  strokeRim(ctx, rgba(shade(icing, -0.25), 0.7), size, 0.7);
  // Glossy blobs at the ends of the longest drips.
  ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
  ctx.beginPath();
  for (const dr of drips) {
    if (dr.len < size.unit * 0.028) continue;
    const y = crestYAt(crest, dr.x);
    if (y !== null) circlePath(ctx, dr.x - dr.w * 0.2, y + thick + dr.len * 0.55, dr.w * 0.18);
  }
  ctx.fill();
}

/** Linear resampling of a left-to-right polyline at a fixed x step. */
function resample(line: readonly Point[], step: number): Point[] {
  const out: Point[] = [];
  const first = line[0];
  const last = line[line.length - 1];
  if (!first || !last) return out;
  let i = 1;
  for (let x = first.x; x <= last.x; x += step) {
    while (i < line.length - 1 && line[i]!.x < x) i++;
    const a = line[i - 1]!;
    const b = line[i]!;
    out.push({ x, y: lerp(a.y, b.y, b.x === a.x ? 0 : clamp((x - a.x) / (b.x - a.x), 0, 1)) });
  }
  out.push({ x: last.x, y: last.y });
  return out;
}

function crestYAt(crest: readonly Point[], x: number): number | null {
  for (let i = 1; i < crest.length; i++) {
    const a = crest[i - 1]!;
    const b = crest[i]!;
    if (x >= a.x && x <= b.x) return lerp(a.y, b.y, b.x === a.x ? 0 : (x - a.x) / (b.x - a.x));
  }
  return null;
}

/** Strata (mesa, table) or terrace field edges, clipped to the ridge body. */
function paintStripes(ctx: Ctx, layer: RidgesLayer, crest: readonly Point[], size: SceneSize, seed: number, color: string): void {
  const base = layer.baseline * size.height;
  const h = layer.height * size.height;
  ctx.save();
  ridgeBody(ctx, crest, size.height + 2);
  ctx.clip();
  ctx.lineCap = 'round';
  if (layer.shape === 'terraces') {
    // Follow the crest downwards, flattening a little each step: stepped fields.
    const gap = Math.max(4, size.unit * 0.028);
    for (let k = 1; k <= 9; k++) {
      const pts = crest.map((p) => ({ x: p.x, y: lerp(p.y, base - h * 0.35, Math.min(0.8, k * 0.07)) + k * gap }));
      const lw = Math.max(1.5, size.unit * 0.008);
      ctx.beginPath();
      smoothLine(ctx, pts);
      ctx.strokeStyle = color;
      ctx.lineWidth = lw;
      ctx.stroke();
      ctx.beginPath();
      smoothLine(
        ctx,
        pts.map((p) => ({ x: p.x, y: p.y + lw * 0.9 })),
      );
      ctx.strokeStyle = rgba(rimOf(layer.color), 0.35);
      ctx.lineWidth = Math.max(1, size.unit * 0.003);
      ctx.stroke();
    }
  } else {
    const gap = Math.max(5, h * 0.16);
    ctx.strokeStyle = rgba(color, 0.9);
    ctx.lineWidth = Math.max(1.5, size.unit * 0.01);
    for (let k = 1, y = base - h + gap * 0.7; y < size.height; k++, y += gap * (0.8 + 0.4 * hash01(seed + 51, k))) {
      ctx.beginPath();
      const pts: Point[] = [];
      for (let x = -size.bleed; x <= size.width + size.bleed; x += size.unit * 0.05) {
        pts.push({ x, y: y + size.unit * 0.006 * valueNoise(seed + 53 + k, x / size.unit / 0.12) });
      }
      smoothLine(ctx, pts);
      ctx.stroke();
    }
  }
  ctx.restore();
}

export const ridgesPainter: Painter<RidgesLayer> = {
  extent: (layer, { size }) => ({ top: (layer.baseline - layer.height) * size.height - size.unit * 0.03, bottom: size.height }),
  still(ctx, layer, { size, seed, palette }) {
    const crest = ridgeCrest(layer, size, seed);
    const base = layer.baseline * size.height;
    const h = layer.height * size.height;
    const top = base - h;
    const bottom = size.height + 2;

    ridgeBody(ctx, crest, bottom);
    ctx.fillStyle = vGradient(ctx, top, Math.min(bottom, base + h * 0.6), [shade(layer.color, 0.12), layer.color, shade(layer.color, -0.08)]);
    ctx.fill();

    if (layer.stripes) paintStripes(ctx, layer, crest, size, seed, layer.stripes);
    if (layer.snow) paintSnow(ctx, layer, crest, size, seed, layer.snow);

    // Soft haze rising from the foot, in the sky's horizon colour.
    const mist = clamp(layer.mist ?? 0, 0, 1);
    if (mist > 0) {
      ctx.save();
      ridgeBody(ctx, crest, bottom);
      ctx.clip();
      ctx.fillStyle = vGradient(ctx, top + h * 0.2, base + 1, [rgba(palette.skyBottom, 0), [0.7, rgba(palette.skyBottom, mist * 0.7)], rgba(palette.skyBottom, mist)]);
      ctx.fillRect(-size.bleed - 4, top, size.width + size.bleed * 2 + 8, bottom - top);
      ctx.restore();
    }

    // Crest: a light sheen just under a darker rim.
    ctx.beginPath();
    smoothLine(
      ctx,
      crest.map((p) => ({ x: p.x, y: p.y + size.unit * 0.006 })),
    );
    ctx.strokeStyle = rgba(shade(layer.color, 0.4), 0.55);
    ctx.lineWidth = Math.max(1, size.unit * 0.005);
    ctx.stroke();
    ctx.beginPath();
    smoothLine(ctx, crest);
    strokeRim(ctx, layer.rim ?? rimOf(layer.color), size);

    if (layer.icing) paintIcing(ctx, crest, size, seed, layer.icing);
  },
};
